import { useMemo } from 'react';
import { IntegrationProvider, useCustomQuery } from '../../graphql';

/** A counter routine an integration writes for the asset in question. */
export interface IntegrationManagedRoutine {
  workspaceRoutineId: string;
  provider: IntegrationProvider;
}

/**
 * Counter routines fed by an integration for one asset, keyed by routine id.
 *
 * Empty while loading, so a counter renders editable for that first frame
 * rather than flashing locked and then unlocking.
 */
export const useIntegrationManagedRoutines = (
  workspaceAssetId: string | undefined,
  disabled?: boolean
) => {
  const { data, isLoading } = useCustomQuery({
    query: API_MANAGED_ROUTINES,
    cacheKey: ['integrationManagedRoutines', workspaceAssetId],
    variables: { workspaceAssetId },
    options: {
      enabled: !!workspaceAssetId && !disabled,
    },
  });

  const integrationManagedRoutines = useMemo(() => {
    const rows: IntegrationManagedRoutine[] = data?.integrationManagedRoutines ?? [];
    return new Map(rows.map((row) => [row.workspaceRoutineId, row]));
  }, [data]);

  return { integrationManagedRoutines, isLoading };
};

const API_MANAGED_ROUTINES = `
  query IntegrationManagedRoutines($workspaceAssetId: ID!) {
    integrationManagedRoutines(workspaceAssetId: $workspaceAssetId) {
      workspaceRoutineId
      provider
    }
  }
`;
